/**
 * React binding for the extraction-run progress store.
 *
 * The store owns polling; this hook registers the surface as a watcher and
 * reads. Passing `analysisId` narrows to a single grid's own extractions, which
 * is what a result grid shows; the Documents page and the app-shell indicator
 * pass none and see every extraction the project has in flight, with
 * acknowledgement scoped to the current viewer.
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { subscribeAuthRenewed } from '@/lib/auth-events';
import {
  extractionRunStore,
  type ExtractionRun,
  type ExtractionRunProjectState,
} from '@/lib/extraction-runs/extraction-run-store';

const EMPTY_STATE: ExtractionRunProjectState = Object.freeze({
  runs: Object.freeze([]),
  progressUnavailable: false,
});

export interface ExtractionRunsView {
  readonly runs: readonly ExtractionRun[];
  readonly progressUnavailable: boolean;
}

export function useExtractionRuns(
  projectId: string | null,
  analysisId?: string | null,
): ExtractionRunsView {
  const { user } = useAuth();
  const [authGeneration, setAuthGeneration] = useState(0);
  const viewerKey = user === null ? null : `${user.tenant_id}:${user.user_id}`;
  const watchedProjectId =
    viewerKey !== null && projectId !== null && projectId.length > 0 ? projectId : null;
  const watchedAnalysisId = analysisId ?? null;

  useEffect(
    () =>
      subscribeAuthRenewed(() => {
        setAuthGeneration((generation) => generation + 1);
      }),
    [],
  );

  useEffect(() => {
    if (watchedProjectId === null || viewerKey === null) {
      return;
    }
    return extractionRunStore.watch(watchedProjectId, { viewerKey });
  }, [watchedProjectId, viewerKey, authGeneration]);

  const state = useSyncExternalStore(
    extractionRunStore.subscribe,
    () =>
      watchedProjectId === null || viewerKey === null
        ? EMPTY_STATE
        : extractionRunStore.getProjectState(watchedProjectId, viewerKey),
    () => EMPTY_STATE,
  );

  return useMemo(() => {
    const runs =
      watchedAnalysisId === null
        ? state.runs
        : state.runs.filter((run) => run.analysisId === watchedAnalysisId);
    return { runs, progressUnavailable: state.progressUnavailable };
  }, [state, watchedAnalysisId]);
}
